import { DOCUMENT } from '@angular/common';
import { Injectable, Signal, inject, signal } from '@angular/core';

/** How fast the tab title alternates while there is unread activity. Slow enough to
 *  read the count, fast enough to catch peripheral vision. */
export const TITLE_FLASH_INTERVAL_MS = 1200;

/**
 * The console's "something arrived" affordance (AB#1884).
 *
 * Agents were missing incoming activity entirely: with no auto-refresh (AB#1866)
 * and no signal, noticing a new ticket meant actively watching and manually
 * reloading. This owns the two signals that fix that:
 *
 *  - an unread COUNT the inbox renders as a badge, and
 *  - a flashing browser-tab title, but ONLY while the tab is hidden — flashing the
 *    title of a tab the agent is already looking at is noise, and the badge is the
 *    better affordance there.
 *
 * Deliberately no sound. Browser autoplay policy blocks audio without a prior user
 * gesture, so a sound-based alert would fire inconsistently and read as a bug; if
 * agents want one it should be an explicit opt-in preference, not a default.
 *
 * The count survives the agent returning to the tab — coming back is not the same as
 * having READ the new rows, so only `clear()` (opening a ticket, or an explicit
 * dismiss) zeroes it.
 */
@Injectable({ providedIn: 'root' })
export class SupportNotificationService {
  private doc = inject(DOCUMENT);

  private _unseen = signal(0);
  readonly unseen: Signal<number> = this._unseen.asReadonly();

  private flashHandle?: ReturnType<typeof setInterval>;
  /** The console's real title, captured when flashing starts so it can always be put
   *  back verbatim — never reconstructed from a constant that could drift from
   *  index.html. */
  private baseTitle?: string;
  private showingAlert = false;

  constructor() {
    this.doc.addEventListener('visibilitychange', () => {
      if (this.doc.visibilityState === 'hidden') {
        // Left again with activity still unread — resume the flash.
        if (this._unseen() > 0) this.startFlashing();
      } else {
        this.stopFlashing();
      }
    });
  }

  /**
   * Record `count` newly-arrived TICKETS — the inbox's own arrivals. Increments the
   * badge AND flashes. A zero/negative count is inert, so a refresh that found nothing
   * new can call this unconditionally.
   */
  noteTickets(count: number): void {
    if (count <= 0) return;
    this._unseen.update((n) => n + count);
    if (this.doc.visibilityState === 'hidden') this.startFlashing();
  }

  /**
   * Record activity on a thread the agent already has OPEN — flashes, but does NOT
   * touch the badge.
   *
   * The distinction is load-bearing, not tidiness. This service is a root singleton and
   * the ONLY renderer of `unseen()` is the queue inbox, labelled "N new" with the
   * tooltip "Tickets that arrived since you last looked". So a single shared counter
   * meant: agent opens a ticket, reads two customer replies that the poll renders on
   * screen, navigates back to the inbox — and the badge claims "2 new" with zero new
   * tickets and zero highlighted rows. It was reporting messages the agent had just
   * read, as arrived tickets. (code-review, PR #1571.)
   *
   * Messages on an open thread need no badge — they are already on screen. What they
   * need is the tab-title flash for the case where the agent is looking elsewhere, and
   * that is exactly what this does. The flashed title deliberately carries the ticket
   * badge count rather than this one, so it never claims a number the inbox cannot
   * account for; when nothing is unseen it shows a plain activity notice.
   */
  noteThreadActivity(count: number): void {
    if (count <= 0) return;
    if (this.doc.visibilityState === 'hidden') this.startFlashing();
  }

  /** The agent has looked: drop the badge and put the title back. */
  clear(): void {
    this._unseen.set(0);
    this.stopFlashing();
  }

  private startFlashing(): void {
    if (this.flashHandle !== undefined) return; // already flashing; note() just raised the count
    this.baseTitle = this.doc.title;
    this.flashHandle = setInterval(() => {
      this.showingAlert = !this.showingAlert;
      // Show the count ONLY when there are unseen tickets to count. Thread activity
      // flashes without incrementing the badge (see noteThreadActivity), so a count
      // here would either be zero or would name a number the inbox cannot account for.
      const unseen = this._unseen();
      this.doc.title = this.showingAlert
        ? (unseen > 0 ? `(${unseen}) New support activity` : 'New support activity')
        : (this.baseTitle as string);
    }, TITLE_FLASH_INTERVAL_MS);
  }

  private stopFlashing(): void {
    if (this.flashHandle === undefined) return;
    clearInterval(this.flashHandle);
    this.flashHandle = undefined;
    this.showingAlert = false;
    if (this.baseTitle !== undefined) this.doc.title = this.baseTitle;
  }
}
